import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, FormControlLabel } from "@mui/material";
import {
	CustomButton,
	CustomDialog,
	CustomTextField,
	icon,
	LimitedTextField,
	Selector,
} from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import { useApi } from "../../../../../../common/hooks";
import { CommitmentDefinitionService } from "../../../../../../api";
import AgentSidePanel from "../../../dsi-form-studio/components/agent-panel/AgentSidePanel";
import styles from "./CommitmentDefinitionCreation.module.css";

export default function CommitmentDefinitionCreation({ definition = null, open, onClose, onRefresh }) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [searchStrategy, setSearchStrategy] = useState("");
	const [contentExamples, setContentExamples] = useState("");
	const [themeId, setThemeId] = useState(null);
	const [isReady, setIsReady] = useState(false);
	const [agent, setAgent] = useState(null);
	const [openAgentSidePanel, setOpenAgentSidePanel] = useState(false);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [themes, setThemes] = useState([]);

	const { call: createDefinition } = useApi(CommitmentDefinitionService.createCommitmentDefinition);
	const { call: updateDefinition } = useApi(CommitmentDefinitionService.updateCommitmentDefinition);
	const { call: getDefinition } = useApi(CommitmentDefinitionService.getCommitmentDefinition);
	const { call: getAllThemes } = useApi(CommitmentDefinitionService.getAllThemes);

	const isEditing = useMemo(() => !!definition?.id, [definition]);

	const themeOptions = useMemo(
		() =>
			themes
				.filter((t) => !t.isDeprecated)
				.sort((a, b) => (a.rank || 0) - (b.rank || 0))
				.map((t) => ({ id: t.id, label: t.name })),
		[themes]
	);

	const selectedTheme = useMemo(() => themeOptions.find((t) => t.id === themeId) || null, [themeOptions, themeId]);

	const isValidForm = useMemo(
		() =>
			name?.length > 0 &&
			description?.length > 0 &&
			searchStrategy?.length > 0 &&
			contentExamples?.length > 0 &&
			themeId !== null,
		[name, description, searchStrategy, contentExamples, themeId]
	);

	useEffect(() => {
		if (open) {
			getAllThemes()
				.then((data) => {
					setThemes(data || []);
				})
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (definition?.id) {
			getDefinition({ id: definition.id })
				.then((data) => {
					setName(data.name || "");
					setDescription(data.description || "");
					setSearchStrategy(data.searchStrategy || "");
					setContentExamples(data.contentExamples || "");
					setThemeId(data.themeId || null);
					setIsReady(data.isReady || false);
					if (data.agentId) {
						setAgent({ id: data.agentId, title: data.agentName || definition.agentName });
					}
				})
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [definition?.id]);

	const resetState = () => {
		setName("");
		setDescription("");
		setSearchStrategy("");
		setContentExamples("");
		setThemeId(null);
		setIsReady(false);
		setAgent(null);
		setOpenConfirmation(false);
	};

	const handleDefinitiveClose = () => {
		resetState();
		onClose();
	};

	const handleClose = () => {
		if (name || description || searchStrategy || contentExamples || themeId) {
			setOpenConfirmation(true);
		} else {
			handleDefinitiveClose();
		}
	};

	const handleSubmit = () => {
		if (!isValidForm) {
			return;
		}

		const payload = {
			name,
			description,
			searchStrategy,
			contentExamples,
			themeId,
			isReady: agent?.id ? isReady : false,
			agentId: agent?.id || null,
		};

		const apiCall = isEditing ? updateDefinition({ id: definition.id }, payload) : createDefinition(payload);

		apiCall
			.then(() => {
				if (onRefresh && typeof onRefresh === "function") {
					onRefresh();
				}
				handleDefinitiveClose();
			})
			.catch(console.error);
	};

	const handleOpenAgentSelection = () => {
		setOpenAgentSidePanel((prev) => !prev);
	};

	const handleChangeAgent = (newAgent) => {
		setAgent(newAgent ? { id: newAgent.id, title: newAgent.title } : null);
		if (!newAgent?.id) {
			setIsReady(false);
		}
		setOpenAgentSidePanel(false);
	};

	const handleToggleReady = () => {
		if (!agent?.id && !isReady) {
			return;
		}
		setIsReady((prev) => !prev);
	};

	const handleChangeTheme = (e) => {
		const { value } = e.target;
		setThemeId(value?.id || null);
	};

	const renderCustomFooter = () => (
		<div className={styles.customFooter}>
			<CustomButton color="secondary" variant="outlined" onClick={handleClose}>
				{translate("common:btn.cancel")}
			</CustomButton>
			<CustomButton variant="outlined" onClick={handleOpenAgentSelection}>
				{agent?.id && agent?.title ? agent.title : translate("dsi.commitment-definition.select-agent")}
			</CustomButton>
			<CustomButton color="primary" disabled={!isValidForm} variant="contained" onClick={handleSubmit}>
				{translate("common:btn.submit")}
			</CustomButton>
		</div>
	);

	return (
		<>
			<CustomDialog
				disableBackdropClick
				disableEnforceFocus
				disableEscapeKeyDown
				displayCloseCross
				isForm
				className={styles.commitmentDefinitionCreation}
				classNameDialog={`${styles.commitmentDefinitionCreationDialog} ${
					openAgentSidePanel && styles["commitmentDefinitionCreation--side-panel-open"]
				}`}
				customFooter={renderCustomFooter}
				open={open}
				Paperprops={{ sx: { width: "50vw", height: "90vh", maxWidth: "none" } }}
				submitDisabled={!isValidForm}
				submitLabel={translate("common:btn.validate")}
				title={translate(isEditing ? "dsi.commitment-definition.edit" : "dsi.commitment-definition.create")}
				onClose={handleClose}
				onSubmit={handleSubmit}
			>
				<div className={styles.dialog__container}>
					<div className={styles.fieldGroup}>
						<span className={styles.fieldLabel}>{translate("common:field.name")}</span>
						<CustomTextField
							fullWidth
							placeholder={translate("common:field.name")}
							size="small"
							value={name}
							variant="outlined"
							onChange={(e) => setName(e.target.value)}
						/>
					</div>

					<div className={styles.fieldGroup}>
						<span className={styles.fieldLabel}>{translate("dsi.commitment-definition.field.theme")}</span>
						<Selector
							fullWidth
							field="label"
							items={themeOptions}
							label={!selectedTheme ? translate("dsi.commitment-definition.field.theme") : ""}
							value={selectedTheme}
							onChange={handleChangeTheme}
						/>
					</div>

					<div>
						<span className={styles.fieldLabel}>
							{translate("dsi.commitment-definition.field.description")}
						</span>
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							maxLength={5000}
							placeholder={translate("dsi.commitment-definition.field.description")}
							showHelper={false}
							value={description}
							variant="outlined"
							onChange={setDescription}
						/>
					</div>

					<div>
						<span className={styles.fieldLabel}>
							{translate("dsi.commitment-definition.field.search-strategy")}
						</span>
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							maxLength={5000}
							placeholder={translate("dsi.commitment-definition.field.search-strategy")}
							showHelper={false}
							value={searchStrategy}
							variant="outlined"
							onChange={setSearchStrategy}
						/>
					</div>

					<div>
						<span className={styles.fieldLabel}>
							{translate("dsi.commitment-definition.field.content-examples")}
						</span>
					</div>
					<div data-has-textfield>
						<LimitedTextField
							fullWidth
							multiline
							className={styles.textField}
							maxLength={5000}
							placeholder={translate("dsi.commitment-definition.field.content-examples")}
							showHelper={false}
							value={contentExamples}
							variant="outlined"
							onChange={setContentExamples}
						/>
					</div>

					<div className={styles.fieldGroup}>
						<FormControlLabel
							control={<Checkbox checked={isReady} disabled={!agent?.id} onChange={handleToggleReady} />}
							label={translate("dsi.commitment-definition.field.enable-detection")}
						/>
					</div>
				</div>
				<CustomDialog
					iconColor="var(--red-500)"
					iconModel={icon.faExclamationTriangle}
					open={openConfirmation}
					submitColor="destructive"
					submitLabel={translate("common:btn.confirm")}
					subTitle={translate("dsi.commitment-definition.cancel-confirmation")}
					onClose={() => setOpenConfirmation(false)}
					onSubmit={handleDefinitiveClose}
				/>
			</CustomDialog>
			<AgentSidePanel
				agent={{ agentId: agent?.id, agentTitle: agent?.title }}
				open={openAgentSidePanel}
				onClose={handleOpenAgentSelection}
				onSubmit={handleChangeAgent}
			/>
		</>
	);
}

CommitmentDefinitionCreation.propTypes = {
	definition: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		agentName: PropTypes.string,
	}),
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onRefresh: PropTypes.func.isRequired,
};

CommitmentDefinitionCreation.defaultProps = {
	definition: null,
};
